import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';

/**
 * ShiftHoverProvider - Isolated context for time interval hover state
 *
 * PERFORMANCE OPTIMIZATION:
 * This context is intentionally separated from ShiftUIProvider to prevent
 * cascading re-renders. The hoveredTimeIntervalIndex changes on every mouse
 * move over HourCells. By isolating this state, only TimeHeader (which displays
 * the hover highlight) re-renders - not the 3000+ other components that use
 * ShiftUIProvider.
 *
 * Additional optimizations:
 * 1. Throttling via requestAnimationFrame - prevents 60+ state updates/sec
 * 2. Deduplication - only updates when value actually changes
 * 3. Separated read/write hooks - setters don't cause re-renders
 *
 * Before: Mouse move → ShiftUIContext update → 3000 component re-renders
 * After:  Mouse move → ShiftHoverContext update → 1 component re-render (TimeHeader)
 */

interface ShiftHoverContextValue {
  hoveredTimeIntervalIndex: number | null;
  setHoveredTimeIntervalIndex: (index: number | null) => void;
}

const ShiftHoverContext = createContext<ShiftHoverContextValue | undefined>(undefined);

interface ShiftHoverProviderProps {
  children: ReactNode;
}

export const ShiftHoverProvider: React.FC<ShiftHoverProviderProps> = ({ children }) => {
  const [hoveredTimeIntervalIndex, setHoveredTimeIntervalIndexState] = useState<number | null>(null);

  // PERFORMANCE: Track last value to avoid redundant state updates
  const lastValueRef = useRef<number | null>(null);
  // PERFORMANCE: Track pending RAF to avoid queueing multiple frames
  const rafIdRef = useRef<number | null>(null);
  // PERFORMANCE: Track pending value for RAF callback
  const pendingValueRef = useRef<number | null>(null);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  // PERFORMANCE: Throttled setter using requestAnimationFrame
  // Only updates state when value changes, max once per frame (~16ms)
  const setHoveredTimeIntervalIndex = useCallback((index: number | null) => {
    // Skip if value hasn't changed
    if (index === lastValueRef.current) {
      return;
    }

    // Store the pending value
    pendingValueRef.current = index;

    // If we already have a RAF scheduled, let it handle the update
    if (rafIdRef.current !== null) {
      return;
    }

    // Schedule the state update for the next animation frame
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const valueToSet = pendingValueRef.current;

      // Double-check value changed (might have been set back to original)
      if (valueToSet !== lastValueRef.current) {
        lastValueRef.current = valueToSet;
        setHoveredTimeIntervalIndexState(valueToSet);
      }
    });
  }, []);

  // Value object is recreated on state change, but only TimeHeader subscribes to this
  const value: ShiftHoverContextValue = {
    hoveredTimeIntervalIndex,
    setHoveredTimeIntervalIndex,
  };

  return (
    <ShiftHoverContext.Provider value={value}>
      {children}
    </ShiftHoverContext.Provider>
  );
};

/**
 * Hook to access hover state - use ONLY in components that need hover highlighting
 * Currently: TimeHeader (read), HourCell (write), VirtualDragLayer (write)
 */
export const useShiftHover = (): ShiftHoverContextValue => {
  const context = useContext(ShiftHoverContext);
  if (context === undefined) {
    throw new Error('useShiftHover must be used within a ShiftHoverProvider');
  }
  return context;
};

/**
 * Hook to ONLY set hover state - prevents re-renders in components that don't read hover state
 * Use this in HourCell and VirtualDragLayer which only need to SET the index, not READ it
 */
export const useSetShiftHover = (): ((index: number | null) => void) => {
  const context = useContext(ShiftHoverContext);
  if (context === undefined) {
    throw new Error('useSetShiftHover must be used within a ShiftHoverProvider');
  }
  return context.setHoveredTimeIntervalIndex;
};
